import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Session-scoped global filter state for the Tags Manager list page.
 * Mirrors the Backbone pattern (`backboneDetailsSlice`): empty array =
 * "no filter for this dimension". Cross-field logic is AND, within-field
 * is OR — handled by the `matches*` helpers in `TagsData.tsx`.
 *
 * Not persisted across reloads (intentional: session-only). The
 * `clearTagsManagerFilters` reducer can be dispatched on teardown if a
 * future page needs to reset the bar (the list page itself doesn't need
 * to call it explicitly — leaving the page just hides the bar).
 */
interface TagsManagerFiltersState {
  /** Applied tag ids. Empty = no filter (show all tags). */
  tagFilterSelection: string[];
  /**
   * Applied categories. Empty = no filter. The string `__no_category__`
   * is a sentinel: when present in the selection, rows whose `category`
   * is null/empty are included.
   */
  categoryFilterSelection: string[];
  /**
   * Applied product internal names — `Kontrol Edge`, `CTX Reflector`,
   * `CTX Reflector V2`. Matches against the tag's productSpread (any of
   * the products the tag's resource assignments span).
   */
  productFilterSelection: string[];
}

export const TAGS_MANAGER_NO_CATEGORY_SENTINEL = "__no_category__";

const initialState: TagsManagerFiltersState = {
  tagFilterSelection: [],
  categoryFilterSelection: [],
  productFilterSelection: [],
};

const tagsManagerFiltersSlice = createSlice({
  name: "tagsManagerFilters",
  initialState,
  reducers: {
    setTagFilterSelection: (state, action: PayloadAction<string[]>) => {
      state.tagFilterSelection = [...action.payload];
    },
    setCategoryFilterSelection: (state, action: PayloadAction<string[]>) => {
      state.categoryFilterSelection = [...action.payload];
    },
    setProductFilterSelection: (state, action: PayloadAction<string[]>) => {
      state.productFilterSelection = [...action.payload];
    },
    clearTagsManagerFilters: (state) => {
      state.tagFilterSelection = [];
      state.categoryFilterSelection = [];
      state.productFilterSelection = [];
    },
  },
});

export const {
  setTagFilterSelection,
  setCategoryFilterSelection,
  setProductFilterSelection,
  clearTagsManagerFilters,
} = tagsManagerFiltersSlice.actions;

export default tagsManagerFiltersSlice.reducer;
